/**
 * E3-FO-E — ELC setup checklist (from GET /mod/elc/setup).
 *
 * Shared by the setup page and the "Finish ELC setup" gate screen.
 */
import type { FC } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { CheckCircle2, Circle } from 'lucide-react';

import type { SetupItem } from '../api/elcSetupApi';

const SetupChecklist: FC<{ items: SetupItem[] }> = ({ items }) => {
  const { pathname } = useLocation();
  if (items.length === 0) {
    return <div style={{ fontSize: 13, color: 'var(--color-text-muted)' }}>Nothing to set up.</div>;
  }
  return (
    <ul aria-label="ELC setup checklist" style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 8 }}>
      {items.map((item) => (
        <li
          key={item.key}
          style={{
            display: 'flex', gap: 8, alignItems: 'flex-start', padding: 10, fontSize: 13,
            border: '1px solid var(--color-border)', borderRadius: 4,
          }}
        >
          {item.done
            ? <CheckCircle2 size={16} color="var(--color-success, #16a34a)" aria-label="Done" />
            : <Circle size={16} color="var(--color-text-muted)" aria-label="Not done" />}
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
              <strong>{item.label}</strong>
              <span className={`elc-admin__chip elc-admin__chip--${item.done ? 'ok' : item.required ? 'warn' : 'muted'}`}>
                {item.done ? 'Done' : item.required ? 'Required' : 'Optional'}
              </span>
            </div>
            {item.detail && (
              <div style={{ fontSize: 12, color: 'var(--color-text-muted)', marginTop: 2 }}>{item.detail}</div>
            )}
          </div>
          {!item.done && item.href && item.href !== pathname && (
            <Link className="btn btn--ghost btn--sm" to={item.href} aria-label={`Resolve: ${item.label}`}>
              Resolve
            </Link>
          )}
        </li>
      ))}
    </ul>
  );
};

export default SetupChecklist;
